import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { HoldingClass } from 'types';

import { getHoldingClassById } from 'modules/partners/api/getHoldingClassById';
import { getHoldingClasses } from 'modules/partners/api/getHoldingClasses';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

type UseGetHoldingClassOptionsProps = {
  societyId: string;
  holdingClassId?: string | null;
};

const FIELDS = 'name';

const mapOption = (holdingClass: HoldingClass): CustomAsyncSelectOption => ({
  value: holdingClass._id,
  label: holdingClass.name,
});

const mapOptions = (
  holdingClasses: HoldingClass[]
): CustomAsyncSelectOption[] =>
  holdingClasses.map((holdingClass: HoldingClass) => mapOption(holdingClass));

export const useGetHoldingClassOptions = ({
  societyId,
  holdingClassId,
}: UseGetHoldingClassOptionsProps) => {
  const [selectedHoldingClass, setSelectedHoldingClass] = useState<string>('');
  const [selectedHoldingClassOption, setSelectedHoldingClassOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [holdingClassOptions, setHoldingClassOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleChangeSelectedHoldingClass = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    if (!selectedOption) return;

    setSelectedHoldingClass(selectedOption.value);
    setSelectedHoldingClassOption(selectedOption);
  };

  const handleSearchHoldingClassByTerm = async (searchTerm: string) => {
    if (!searchTerm || !societyId) return;

    try {
      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          params: { fields: FIELDS, searchTerm },
        })
      );

      return mapOptions(holdingClasses);
    } catch (error) {
      setIsError(true);
    }
  };

  const fetchHoldingClasses = useCallback(async () => {
    if (!societyId) return;

    setIsLoading(true);
    setIsError(false);

    try {
      if (holdingClassId) {
        const holdingClass = await store.dispatch(
          getHoldingClassById({
            holdingClassId,
          })
        );

        const selectedOption: CustomAsyncSelectOption = mapOption(holdingClass);

        setSelectedHoldingClassOption(selectedOption);
        setIsLoading(false);
        return;
      }

      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          params: { fields: FIELDS },
        })
      );

      const mappedOptions = mapOptions(holdingClasses);
      setHoldingClassOptions(mappedOptions);

      const firstOption = mappedOptions[0] || null;
      setSelectedHoldingClass(firstOption.value);
      setSelectedHoldingClassOption(firstOption);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [holdingClassId, societyId]);

  useEffect(() => {
    fetchHoldingClasses();
  }, [fetchHoldingClasses]);

  return {
    data: {
      selectedHoldingClass,
      selectedHoldingClassOption,
      holdingClassOptions,
    },
    actions: {
      handleChangeSelectedHoldingClass,
      handleSearchHoldingClassByTerm,
    },
    isLoading,
    isError,
  };
};
